import { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  size: number;
  speedY: number;
  speedX: number;
  opacity: number;
  opacityDelta: number;
  type: 'dot' | 'star';
}

export default function HeroParticles() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animFrameId: number;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();

    const PARTICLE_COUNT = 55;

    const createParticle = (): Particle => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      size: Math.random() * 1.8 + 0.4,
      speedY: -(Math.random() * 0.35 + 0.1),
      speedX: (Math.random() - 0.5) * 0.15,
      opacity: Math.random() * 0.35 + 0.05,
      opacityDelta: (Math.random() * 0.003 + 0.001) * (Math.random() < 0.5 ? 1 : -1),
      type: Math.random() < 0.25 ? 'star' : 'dot',
    });

    const particles: Particle[] = Array.from({ length: PARTICLE_COUNT }, createParticle);

    const drawStar = (cx: number, cy: number, r: number, alpha: number) => {
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = '#C9A440';
      ctx.lineWidth = r * 0.6;
      ctx.lineCap = 'round';
      const arms = 4;
      for (let i = 0; i < arms; i++) {
        const angle = (i / arms) * Math.PI;
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(angle) * r * 1.8, cy + Math.sin(angle) * r * 1.8);
        ctx.lineTo(cx - Math.cos(angle) * r * 1.8, cy - Math.sin(angle) * r * 1.8);
        ctx.stroke();
      }
      ctx.restore();
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (const p of particles) {
        p.x += p.speedX;
        p.y += p.speedY;
        p.opacity += p.opacityDelta;

        if (p.opacity <= 0.03) p.opacityDelta = Math.abs(p.opacityDelta);
        if (p.opacity >= 0.45) p.opacityDelta = -Math.abs(p.opacityDelta);

        if (p.y < -10) {
          p.y = canvas.height + 10;
          p.x = Math.random() * canvas.width;
        }
        if (p.x < -10) p.x = canvas.width + 10;
        if (p.x > canvas.width + 10) p.x = -10;

        if (p.type === 'star') {
          drawStar(p.x, p.y, p.size, p.opacity);
        } else {
          ctx.beginPath();
          const gradient = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * 2.5);
          gradient.addColorStop(0, `rgba(201, 164, 64, ${p.opacity})`);
          gradient.addColorStop(0.5, `rgba(218, 185, 80, ${p.opacity * 0.6})`);
          gradient.addColorStop(1, `rgba(201, 164, 64, 0)`);
          ctx.fillStyle = gradient;
          ctx.arc(p.x, p.y, p.size * 2.5, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      animFrameId = requestAnimationFrame(animate);
    };

    animate();

    const observer = new ResizeObserver(resize);
    observer.observe(canvas);

    return () => {
      cancelAnimationFrame(animFrameId);
      observer.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ zIndex: 2 }}
    />
  );
}
